// pages/CompetencyQuiz/QuizAttempt.js
//
// Round Training-1 (2026-10-26) — taking the Training Quiz.
//
//   • One question at a time; the palette jumps to any question (answered = filled,
//     flagged = amber outline, not saved = red).
//   • Every change is saved at once (PUT …/answer), one request at a time per question and
//     always the newest value, so a slow request can never overwrite a later click. A save
//     that fails is marked "not saved", kept, and retried; Submit waits for pending saves
//     first and says how many could not be saved.
//   • The countdown runs to a fixed deadline taken from the server's SecondsRemaining (so a
//     throttled background tab cannot slow it) and re-syncs with the server whenever the tab
//     becomes visible again. At 0 the attempt is submitted (retried on failure); the server
//     would submit it anyway on the next read, so leaving the page cannot beat the limit.
//   • The page never receives correct answers; scoring happens on submit.
//
// Backend: GET /api/competency-quiz/attempts/{id}, PUT …/{id}/answer, POST …/{id}/submit
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Badge, Button, Card, CardBody, Col, Container, Input, Label,
    Modal, ModalBody, ModalFooter, ModalHeader, Progress, Row, Spinner,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DashboardPageHeader, EmptyState } from "../../Components/Common/DashboardKit";
import { errorText, getAttempt, pick, saveAnswer, submitAttempt } from "../../services/competencyQuizApi";
import { refreshSession } from "../../api";
import { fmtClock } from "./quizFormat";
import useAuth from "../Personal Attendance/useAuth";

const normalizeQuestion = (q) => ({
    questionId:   pick(q, "QuestionId", "questionId"),
    order:        pick(q, "DisplayOrder", "displayOrder"),
    code:         pick(q, "QuestionCode", "questionCode"),
    domain:       pick(q, "Domain", "domain"),
    type:         pick(q, "QuestionType", "questionType") || "single",
    text:         pick(q, "QuestionText", "questionText"),
    difficulty:   pick(q, "Difficulty", "difficulty"),
    critical:     !!pick(q, "IsCritical", "isCritical"),
    source:       pick(q, "SourceDocument", "sourceDocument"),
    section:      pick(q, "SourceSection", "sourceSection"),
    options:      (pick(q, "Options", "options") || []).map((o) => ({ key: pick(o, "Key", "key"), text: pick(o, "Text", "text") })),
    selected:     pick(q, "SelectedKeys", "selectedKeys") || [],
    flagged:      !!pick(q, "IsFlagged", "isFlagged"),
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function QuizAttempt() {
    document.title = "Training Quiz | SSW Technologies";
    const { attemptId } = useParams();
    const navigate = useNavigate();
    const { isItAdmin, canAccessModule } = useAuth();
    const hubOk = isItAdmin || canAccessModule("competencyQuiz.any");
    const backTo = hubOk ? "/competency-quiz" : "/dashboard";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [title, setTitle] = useState("");
    const [questions, setQuestions] = useState([]);
    const [idx, setIdx] = useState(0);
    const [deadline, setDeadline] = useState(null);          // ms timestamp; null = no time limit
    const [secondsLeft, setSecondsLeft] = useState(null);
    const [savingCount, setSavingCount] = useState(0);
    const [unsaved, setUnsaved] = useState(() => new Set());  // question ids whose last save failed (retrying)
    const [failed, setFailed] = useState(() => new Map());    // question id -> message: rejected by the server, not retried
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const mountedRef = useRef(true);
    const submittedRef = useRef(false);
    const pendingRef = useRef(new Map());    // questionId -> { selected?, flagged? } not yet sent
    const inFlightRef = useRef(new Set());   // questionIds with a request on the wire
    const retryTimersRef = useRef(new Map());
    const autoFiredRef = useRef(false);      // the 0:00 auto-submit runs once per page
    const pollRef = useRef(null);
    const attemptIdRef = useRef(attemptId);
    attemptIdRef.current = attemptId;
    const deadlineRef = useRef(null);
    const forceSubmitRef = useRef(false);    // a second Submit press goes ahead without unsaved answers
    const resyncingRef = useRef(false);      // one re-check at a time after a refused save
    const navigatingRef = useRef(false);     // goToResult runs once

    useEffect(() => {
        mountedRef.current = true;
        const timers = retryTimersRef.current;
        const pending = pendingRef.current;
        return () => {
            mountedRef.current = false;
            timers.forEach((t) => clearTimeout(t));
            clearInterval(pollRef.current);
            // Leaving the page inside the app: still send answers that were queued or waiting to retry.
            // A question with a request still on the wire sends its newer value after that request ends
            // (see flush), so the two never race.
            pending.forEach((patch, qid) => {
                if (inFlightRef.current.has(qid)) return;
                saveAnswer(attemptIdRef.current, { questionId: qid, selectedKeys: patch.selected, isFlagged: patch.flagged }).catch(() => {});
                pending.delete(qid);
            });
        };
    }, []);

    // Finishing unassigns the quiz on the server: refresh this session's permissions first, so the
    // Training Quiz link leaves the sidebar now instead of at the next token refresh (IT keeps it).
    const goToResult = useCallback(async () => {
        if (navigatingRef.current) return;
        navigatingRef.current = true;
        try {
            await Promise.race([refreshSession(), new Promise((resolve) => setTimeout(resolve, 4000))]);
        } catch { /* the menu then updates at the next normal refresh */ }
        if (mountedRef.current) navigate(`/competency-quiz/result/${attemptId}`, { replace: true });
    }, [navigate, attemptId]);

    const applyRemaining = useCallback((s) => {
        if (s === undefined || s === null) { deadlineRef.current = null; setDeadline(null); setSecondsLeft(null); return; }
        deadlineRef.current = Date.now() + Number(s) * 1000;
        setDeadline(deadlineRef.current);
        setSecondsLeft(Number(s));
    }, []);

    const markUnsaved = useCallback((qid, isUnsaved) => setUnsaved((set) => {
        if (isUnsaved === set.has(qid)) return set;
        const next = new Set(set);
        if (isUnsaved) next.add(qid); else next.delete(qid);
        return next;
    }), []);

    // ── Load, and re-sync with the server when the tab becomes visible again ──
    const sync = useCallback(async (first) => {
        try {
            const view = await getAttempt(attemptId);
            if (!mountedRef.current) return;
            if (pick(view, "Status", "status") === "Submitted") {
                if (!first && !submittedRef.current) toast.info("Time is up. Your quiz was submitted.");
                submittedRef.current = true;
                goToResult();
                return;
            }
            if (first) {
                setTitle(pick(view, "QuizTitle", "quizTitle") || "Training Quiz");
                setQuestions((pick(view, "Questions", "questions") || []).map(normalizeQuestion));
            }
            applyRemaining(pick(view, "SecondsRemaining", "secondsRemaining"));
        } catch (err) {
            if (first && mountedRef.current) setError(errorText(err, "Could not load the quiz."));
        } finally {
            if (first && mountedRef.current) setLoading(false);
        }
    }, [attemptId, goToResult, applyRemaining]);

    useEffect(() => { sync(true); }, [sync]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible" && !submittedRef.current) sync(false);
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
        };
    }, [sync]);

    // Warn before closing the tab while an answer is still on its way.
    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (inFlightRef.current.size > 0 || pendingRef.current.size > 0) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, []);

    // ── Saving: one request per question at a time, newest value wins ──
    const flush = useCallback(async (qid) => {
        if (inFlightRef.current.has(qid)) return;            // the running request re-flushes when it ends
        const patch = pendingRef.current.get(qid);
        if (!patch) return;
        pendingRef.current.delete(qid);
        inFlightRef.current.add(qid);
        setSavingCount((n) => n + 1);
        let failed = false;
        try {
            const res = await saveAnswer(attemptId, { questionId: qid, selectedKeys: patch.selected, isFlagged: patch.flagged });
            if (!mountedRef.current) return;
            if (pick(res, "Status", "status") === "Submitted") {
                if (!submittedRef.current) toast.info("Time is up. Your quiz was submitted.");
                submittedRef.current = true;
                goToResult();
                return;
            }
            if (!pendingRef.current.has(qid)) markUnsaved(qid, false);
            setFailed((m) => { if (!m.has(qid)) return m; const n = new Map(m); n.delete(qid); return n; });
            applyRemaining(pick(res, "SecondsRemaining", "secondsRemaining"));
        } catch (err) {
            failed = true;
            const status = err?.response?.status;
            if (!mountedRef.current) {
                // Left the page while this was on the wire: one last try with the newest value.
                if (status !== 400 && status !== 403 && status !== 404) {
                    const latest = { ...patch, ...(pendingRef.current.get(qid) || {}) };
                    pendingRef.current.delete(qid);
                    saveAnswer(attemptIdRef.current, { questionId: qid, selectedKeys: latest.selected, isFlagged: latest.flagged }).catch(() => {});
                }
                return;
            }
            if ((status === 403 || status === 404) && !resyncingRef.current) {
                // Usually the attempt was already submitted (and the quiz unassigned): re-check once.
                resyncingRef.current = true;
                sync(false).finally(() => { resyncingRef.current = false; });
            }
            if (status === 400 || status === 403 || status === 404) {
                // The server refused it: retrying would not help. Show why; do not keep it pending.
                const msg = errorText(err, "Your answer was not saved.");
                setFailed((m) => new Map(m).set(qid, msg));
                markUnsaved(qid, false);
                toast.error(msg);
            } else {
                // Network / server hiccup: keep the value (unless a newer click replaced those fields) and retry.
                pendingRef.current.set(qid, { ...patch, ...(pendingRef.current.get(qid) || {}) });
                markUnsaved(qid, true);
                if (!submittedRef.current) {
                    clearTimeout(retryTimersRef.current.get(qid));
                    retryTimersRef.current.set(qid, setTimeout(() => flush(qid), 3000));
                }
            }
        } finally {
            inFlightRef.current.delete(qid);
            if (mountedRef.current) setSavingCount((n) => Math.max(0, n - 1));
            if (!failed && mountedRef.current && pendingRef.current.has(qid)) flush(qid);
            // Unmounted with a newer value queued behind this request: send it now, in order.
            if (!failed && !mountedRef.current && pendingRef.current.has(qid)) {
                const next = pendingRef.current.get(qid);
                pendingRef.current.delete(qid);
                saveAnswer(attemptIdRef.current, { questionId: qid, selectedKeys: next.selected, isFlagged: next.flagged }).catch(() => {});
            }
        }
    }, [attemptId, goToResult, applyRemaining, markUnsaved, sync]);

    const update = (patch) => {
        const q = questions[idx];
        if (!q || submittedRef.current) return;
        setQuestions((list) => list.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
        pendingRef.current.set(q.questionId, { ...(pendingRef.current.get(q.questionId) || {}), ...patch });
        flush(q.questionId);
    };

    const choose = (key) => {
        const q = questions[idx];
        const selected = q.type === "multi"
            ? (q.selected.includes(key) ? q.selected.filter((k) => k !== key) : [...q.selected, key].sort())
            : [key];
        update({ selected });
    };

    /** Sends everything still pending and waits (up to ~8 s) for the requests to finish. */
    const settleSaves = useCallback(async () => {
        [...pendingRef.current.keys()].forEach((qid) => {
            clearTimeout(retryTimersRef.current.get(qid));
            flush(qid);
        });
        const until = Date.now() + 8000;
        while (inFlightRef.current.size > 0 && Date.now() < until) {
            await sleep(150);
        }
        return pendingRef.current.size;   // answers that still could not be saved
    }, [flush]);

    /** Answers that failed while Submit was running were not given a retry timer: give them one. */
    const restartRetries = () => {
        [...pendingRef.current.keys()].forEach((qid) => {
            clearTimeout(retryTimersRef.current.get(qid));
            retryTimersRef.current.set(qid, setTimeout(() => flush(qid), 3000));
        });
    };

    // ── Submit (manual, or at 0:00 with retries) ──
    const submit = useCallback(async (auto = false, tries = 0) => {
        if (submittedRef.current && tries === 0) return;
        submittedRef.current = true;
        setSubmitting(true);
        const notSaved = await settleSaves();
        const timeUp = deadlineRef.current !== null && Date.now() >= deadlineRef.current;
        if (notSaved > 0 && !auto && !timeUp && !forceSubmitRef.current) {
            // Do not lose answers silently: say so once; a second Submit goes ahead without them.
            forceSubmitRef.current = true;
            submittedRef.current = false;
            setSubmitting(false);
            restartRetries();
            toast.warning(`${notSaved} answer${notSaved === 1 ? " has" : "s have"} not been saved (connection problem). Wait a moment and submit again; submitting again now counts ${notSaved === 1 ? "it" : "them"} as unanswered.`, { autoClose: 9000 });
            return;
        }
        try {
            await submitAttempt(attemptId);
            if (auto) toast.info("Time is up. Your quiz was submitted.");
            goToResult();
        } catch (err) {
            if (!mountedRef.current) return;
            const timeIsUp = deadlineRef.current !== null && Date.now() >= deadlineRef.current;
            if ((auto || timeIsUp) && tries < 3) {
                setTimeout(() => submit(true, tries + 1), 3000);
                return;
            }
            if (auto || timeIsUp) {
                // Time is up and the server cannot be reached: keep the answers locked and keep asking
                // the server; once it answers it submits the attempt itself and we move to the result.
                toast.warning("Time is up, but the server cannot be reached. Your quiz will be submitted as soon as the connection is back.", { autoClose: false });
                clearInterval(pollRef.current);
                pollRef.current = setInterval(() => sync(false), 15000);
                return;
            }
            submittedRef.current = false;
            setSubmitting(false);
            restartRetries();
            toast.error(errorText(err, "Could not submit the quiz. Check your connection and try again."));
        }
    }, [attemptId, goToResult, settleSaves, sync]);

    // Countdown to the fixed deadline (recomputed from the clock on every tick).
    useEffect(() => {
        if (deadline === null || loading) return undefined;
        const tick = () => {
            const s = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            setSecondsLeft(s);
            if (s <= 0 && !autoFiredRef.current) {
                autoFiredRef.current = true;
                if (!submittedRef.current) submit(true);
            }
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [deadline, loading, submit]);

    const notSavedCount = unsaved.size + failed.size;
    const answeredCount = useMemo(
        () => questions.filter((q) => q.selected.length > 0 && !unsaved.has(q.questionId) && !failed.has(q.questionId)).length,
        [questions, unsaved, failed]
    );
    // For the submit dialog: questions with no choice at all (not-saved ones are reported separately).
    const unanswered = questions.filter((q) => q.selected.length === 0).length;
    const saving = savingCount > 0;

    if (loading) return <div className="page-content"><div className="text-center py-5"><Spinner size="sm" /> Loading…</div></div>;
    if (error) {
        return (
            <div className="page-content">
                <Container fluid>
                    <EmptyState icon="ri-error-warning-line" tone="danger" title="Quiz unavailable" message={error}
                        action={<Button color="primary" onClick={() => navigate(backTo)}>{hubOk ? "Back to the quiz" : "Back to the dashboard"}</Button>} />
                </Container>
            </div>
        );
    }

    const q = questions[idx];
    const lowTime = secondsLeft !== null && secondsLeft <= 120;

    return (
        <div className="page-content">
            <ToastContainer position="top-right" autoClose={3500} />
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-edit-box-line"
                    eyebrow="Training Quiz"
                    title={title}
                    subtitle="Answers are saved as you go."
                    tone="violet"
                    compact
                    breadcrumb={[{ label: hubOk ? "Training Quiz" : "Dashboard", to: backTo }, { label: "Attempt" }]}
                    actions={
                        secondsLeft !== null && (
                            <Badge color={lowTime ? "danger" : "light"} className={`fs-15 px-3 py-2 ${lowTime ? "" : "text-dark"}`}>
                                <i className="ri-timer-line me-1" />{fmtClock(secondsLeft)}
                            </Badge>
                        )
                    }
                />

                <Row className="g-3">
                    <Col lg={9}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="text-muted small">Question {idx + 1} of {questions.length}</span>
                                    <span className="small">
                                        {failed.size > 0
                                            ? <span className="text-danger"><i className="ri-error-warning-line me-1" />{failed.size} could not be saved</span>
                                            : unsaved.size > 0
                                            ? <span className="text-danger"><i className="ri-error-warning-line me-1" />{unsaved.size} not saved, retrying…</span>
                                            : saving ? <span className="text-muted"><Spinner size="sm" /> Saving…</span>
                                            : <span className="text-muted">{answeredCount} answered</span>}
                                    </span>
                                </div>
                                <Progress value={(answeredCount / Math.max(1, questions.length)) * 100} className="mb-3" style={{ height: 6 }} />

                                {q && (
                                    <>
                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                            <Badge color="light" className="text-dark">{q.domain}</Badge>
                                            {q.critical && <Badge color="danger">Critical</Badge>}
                                            {q.difficulty && <Badge color="light" className="text-muted">{q.difficulty}</Badge>}
                                        </div>
                                        <h5 className="mb-2" style={{ lineHeight: 1.4 }}>{q.text}</h5>
                                        {q.type === "multi" && <div className="small text-primary fw-semibold mb-2">Select all that apply.</div>}

                                        <div className="mb-3">
                                            {q.options.map((o) => {
                                                const checked = q.selected.includes(o.key);
                                                return (
                                                    <Label
                                                        key={o.key}
                                                        className={`d-flex align-items-start gap-2 border rounded p-2 mb-2 ${checked ? "border-primary bg-primary-subtle" : ""}`}
                                                        style={{ cursor: submitting ? "default" : "pointer" }}
                                                    >
                                                        <Input
                                                            type={q.type === "multi" ? "checkbox" : "radio"}
                                                            name={`q-${q.questionId}`}
                                                            checked={checked}
                                                            disabled={submitting}
                                                            onChange={() => choose(o.key)}
                                                            className="mt-1"
                                                        />
                                                        <span><b>{o.key}.</b> {o.text}</span>
                                                    </Label>
                                                );
                                            })}
                                        </div>

                                        {failed.has(q.questionId) ? (
                                            <div className="small text-danger mb-2">This answer could not be saved: {failed.get(q.questionId)}</div>
                                        ) : unsaved.has(q.questionId) && (
                                            <div className="small text-danger mb-2">This answer has not been saved yet. It will be retried automatically.</div>
                                        )}
                                        {(q.source || q.section) && (
                                            <div className="small text-muted mb-3">Source: {q.source}{q.section ? ` — ${q.section}` : ""}</div>
                                        )}

                                        <div className="d-flex justify-content-between flex-wrap gap-2">
                                            <Button color="light" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>
                                                <i className="ri-arrow-left-line me-1" />Previous
                                            </Button>
                                            <div className="d-flex gap-2">
                                                <Button color={q.flagged ? "warning" : "light"} disabled={submitting} onClick={() => update({ flagged: !q.flagged })}>
                                                    <i className="ri-flag-line me-1" />{q.flagged ? "Unflag" : "Flag for review"}
                                                </Button>
                                                {idx < questions.length - 1 ? (
                                                    <Button color="primary" onClick={() => setIdx(idx + 1)}>
                                                        Next<i className="ri-arrow-right-line ms-1" />
                                                    </Button>
                                                ) : (
                                                    <Button color="success" onClick={() => setConfirmOpen(true)} disabled={submitting}>
                                                        <i className="ri-send-plane-line me-1" />Submit quiz
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={3}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <h6 className="mb-2">Questions</h6>
                                <div className="d-flex flex-wrap gap-1 mb-3">
                                    {questions.map((x, i) => {
                                        const notSaved = unsaved.has(x.questionId) || failed.has(x.questionId);
                                        return (
                                            <Button
                                                key={x.questionId}
                                                size="sm"
                                                color={i === idx ? "primary" : notSaved ? "soft-danger" : x.selected.length ? "soft-primary" : "light"}
                                                outline={false}
                                                onClick={() => setIdx(i)}
                                                style={{ width: 38, ...(x.flagged ? { boxShadow: "0 0 0 2px #f7b84b inset" } : {}) }}
                                                title={notSaved ? "Not saved yet" : x.flagged ? "Flagged" : x.selected.length ? "Answered" : "Not answered"}
                                            >
                                                {i + 1}
                                            </Button>
                                        );
                                    })}
                                </div>
                                <div className="small text-muted mb-3">
                                    <div>{answeredCount} answered · {questions.length - answeredCount} left</div>
                                    <div>{questions.filter((x) => x.flagged).length} flagged</div>
                                    {notSavedCount > 0 && <div className="text-danger">{notSavedCount} not saved</div>}
                                </div>
                                <Button color="success" className="w-100" onClick={() => setConfirmOpen(true)} disabled={submitting}>
                                    {submitting ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}Submit quiz
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered>
                <ModalHeader toggle={() => setConfirmOpen(false)}>Submit the quiz?</ModalHeader>
                <ModalBody>
                    {unanswered > 0
                        ? <>You have <b>{unanswered}</b> unanswered question{unanswered === 1 ? "" : "s"}. They will count as wrong.</>
                        : "All questions are answered."}
                    {notSavedCount > 0 && (
                        <div className="text-danger mt-2">
                            {notSavedCount} answer{notSavedCount === 1 ? " has" : "s have"} not been saved.
                            {unsaved.size > 0 ? " Submit tries to save them once more; if that fails you are warned first, and submitting again counts them as unanswered." : " They count as unanswered."}
                        </div>
                    )}
                    <div className="small text-muted mt-2">You cannot change your answers after submitting.</div>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setConfirmOpen(false)}>Keep working</Button>
                    <Button color="success" disabled={submitting} onClick={() => { setConfirmOpen(false); submit(false); }}>
                        {submitting ? <Spinner size="sm" className="me-1" /> : null}Submit
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
